import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import { KaiCoachService } from './kaiCoachService';

// One authenticated KaiCoachService per signed-in identity, shared by the
// conversation hook and the session-prepare hook so both talk to the backend
// the same way (Firebase token, legacy token, auth redirect).
export function useKaiCoachService(): { service: KaiCoachService | null; userId: string } {
  const { getIdToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const service = useMemo(() => {
    if (!user) {
      return null;
    }
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const client = createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: () =>
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
          replace: true,
        }),
    });
    return new KaiCoachService(client);
    // location is intentionally excluded so the client is stable per auth identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIdToken, navigate, user]);

  const userId = useMemo(() => getKuduUserId(user) ?? user?.uid ?? '', [user]);

  return { service, userId };
}
